// src/redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../api/authApi';

const safeJSONParse = (item) => {
  if (!item || item === 'undefined' || item === 'null') {
    return null;
  }
  try {
    return JSON.parse(item);
  } catch (e) {
    return null;
  }
};

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token && token !== 'undefined' && token !== 'null') {
    return token;
  }
  return null;
};

const getUser = () => {
  const user = localStorage.getItem('user');
  if (user && user !== 'undefined' && user !== 'null') {
    return safeJSONParse(user);
  }
  return null;
};

const checkProfileCompleted = () => {
  const user = getUser();
  if (user && user.name && user.name.trim() !== '' && user.name !== 'User' && user.name !== 'Account') {
    return true;
  }
  return localStorage.getItem('profileCompleted') === 'true';
};

const initialState = {
  user: getUser(),
  token: getToken(),
  isAuthenticated: !!getToken(),
  isLoading: false,
  error: null,
  phone: localStorage.getItem('tiffinPhone') || '',
  otpSent: false,
  otpVerified: false,
  message: null,
  isNewUser: localStorage.getItem('isNewUser') === 'true',
  profileCompleted: checkProfileCompleted(),
  showLoginModal: false,
};

// ==================== ASYNC THUNKS ====================

export const sendOTP = createAsyncThunk(
  'auth/sendOTP',
  async (phone, { rejectWithValue }) => {
    try {
      console.log('📞 sendOTP thunk called with:', phone);
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
      }
      const response = await apiService.sendOTP(phone);
      console.log('✅ sendOTP response:', response);
      return response;
    } catch (error) {
      console.error('❌ sendOTP error:', error);
      return rejectWithValue(error.message || 'Failed to send OTP');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      console.log('🔐 verifyOTP thunk called for:', phone);
      const response = await apiService.verifyOTP(phone, otp);
      console.log('✅ verifyOTP response:', response);
      
      // ✅ Immediately fetch profile after OTP verification
      try {
        console.log('🔄 Fetching profile after OTP verification...');
        const profileResponse = await apiService.getUserProfile();
        console.log('✅ Profile fetched after OTP:', profileResponse);
        
        if (profileResponse?.status && profileResponse?.data) {
          // ✅ Merge profile data with OTP response
          response.user_profile = profileResponse.data;
          response.name = profileResponse.data.name || response.name;
          response.address = profileResponse.data.address || response.address;
          response.profile_image = profileResponse.data.profile_image || response.profile_image;
        }
      } catch (profileError) {
        console.warn('⚠️ Could not fetch profile immediately:', profileError);
      }
      
      return response;
    } catch (error) {
      console.error('❌ verifyOTP error:', error);
      return rejectWithValue(error.message || 'Invalid OTP');
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching user profile...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      const response = await apiService.getUserProfile();
      console.log('✅ Profile fetched:', response);
      if (response?.status && response?.data) {
        return response;
      } else {
        return rejectWithValue('No profile data found');
      }
    } catch (error) {
      console.error('❌ Get profile error:', error);
      return rejectWithValue(error.message || 'Failed to get profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData, { rejectWithValue }) => {
    try {
      console.log('📤 Updating user profile...');
      const response = await apiService.updateUserProfile(userData);
      console.log('✅ Profile updated:', response);
      return response;
    } catch (error) {
      console.error('❌ Update profile error:', error);
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.logoutUser();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.deleteUserAccount();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete account');
    }
  }
);

const syncUserFromStorage = createAsyncThunk(
  'auth/syncUserFromStorage',
  async (_, { rejectWithValue }) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');
      console.log('🔄 Syncing user from storage:', userData);
      if (token && userData && userData.name) {
        return { user: userData, token: token };
      }
      return rejectWithValue('No valid user data found');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== SLICE ====================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setPhone: (state, action) => {
      state.phone = action.payload;
      localStorage.setItem('tiffinPhone', action.payload);
    },
    clearOTP: (state) => {
      state.otpSent = false;
      state.otpVerified = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.phone = '';
      state.otpSent = false;
      state.otpVerified = false;
      state.error = null;
      state.isLoading = false;
      state.message = null;
      state.isNewUser = false;
      state.profileCompleted = false;
      state.showLoginModal = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tiffinPhone');
      localStorage.removeItem('tiffinUserLogin');
      localStorage.removeItem('isNewUser');
      localStorage.removeItem('profileCompleted');
    },
    setShowLoginModal: (state, action) => {
      state.showLoginModal = action.payload;
    },
    setProfileCompleted: (state, action) => {
      state.profileCompleted = action.payload;
      localStorage.setItem('profileCompleted', action.payload ? 'true' : 'false');
    },
    updateUserName: (state, action) => {
      const name = action.payload;
      if (state.user) {
        state.user.name = name;
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        userData.name = name;
        localStorage.setItem('user', JSON.stringify(userData));
        const authUser = JSON.parse(localStorage.getItem('tiffinUserLogin') || '{}');
        authUser.name = name;
        localStorage.setItem('tiffinUserLogin', JSON.stringify(authUser));
        state.profileCompleted = true;
        localStorage.setItem('profileCompleted', 'true');
        console.log('✅ User name updated to:', name);
      }
    },
    refreshUser: (state) => {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData && userData.name) {
        state.user = { ...state.user, ...userData };
        state.profileCompleted = true;
        console.log('✅ User refreshed from localStorage:', userData);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== SEND OTP =====
      .addCase(sendOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpSent = true;
        state.phone = action.payload?.phone || state.phone;
        state.error = null;
        state.message = action.payload?.message || 'OTP sent successfully!';
        state.isNewUser = action.payload?.is_new_user || false;
        localStorage.setItem('isNewUser', state.isNewUser ? 'true' : 'false');
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.otpSent = false;
      })
      
      // ===== VERIFY OTP =====
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.otpVerified = true;
        state.otpSent = true;
        state.error = null;
        state.message = action.payload?.message || 'Login successful!';
        state.isNewUser = action.payload?.is_new_user || false;
        state.phone = action.payload?.phone || state.phone;
        state.showLoginModal = false;
        
        if (action.payload?.token) {
          state.token = action.payload.token;
          localStorage.setItem('token', action.payload.token);
        }
        
        const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        // ✅ Get name from profile data (fetched in verifyOTP thunk)
        const profileData = action.payload?.user_profile || {};
        const responseName = profileData?.name || action.payload?.name;
        const existingName = existingUser?.name;
        
        // ✅ Determine the name: use profile name, then response name, then existing name
        let userName = null;
        if (responseName && responseName !== 'User' && responseName !== 'Account' && responseName.trim() !== '') {
          userName = responseName;
        } else if (existingName && existingName !== 'User' && existingName !== 'Account' && existingName.trim() !== '') {
          userName = existingName;
        }
        
        // ✅ If no valid name found, use the phone number as fallback
        if (!userName) {
          userName = state.phone || 'User';
        }
        
        const hasName = userName && userName.trim() !== '' && userName !== 'User' && userName !== 'Account';
        state.profileCompleted = hasName || !state.isNewUser;
        
        const userData = {
          id: action.payload?.user_id || existingUser?.id,
          phone: state.phone,
          is_new_user: state.isNewUser,
          name: userName,
          address: profileData?.address || action.payload?.address || existingUser?.address || '',
          profile_image: profileData?.profile_image || action.payload?.profile_image || existingUser?.profile_image || null,
        };
        state.user = userData;
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('isNewUser', state.isNewUser ? 'true' : 'false');
        localStorage.setItem('profileCompleted', state.profileCompleted ? 'true' : 'false');
        
        localStorage.setItem('tiffinUserLogin', JSON.stringify({
          phone: state.phone,
          name: userName || 'My Account',
          loggedIn: true,
          is_new_user: state.isNewUser,
          profile_completed: state.profileCompleted,
        }));
        
        console.log('✅ User data after OTP verification:', userData);
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.otpVerified = false;
      })
      
      // ===== GET PROFILE =====
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        const profileData = action.payload?.data || action.payload;
        console.log('📦 Profile data received:', profileData);
        
        // ✅ Get the name from profile data
        const profileName = profileData?.name;
        const existingName = state.user?.name;
        
        let userName = null;
        if (profileName && profileName !== 'User' && profileName !== 'Account' && profileName.trim() !== '') {
          userName = profileName;
        } else if (existingName && existingName !== 'User' && existingName !== 'Account' && existingName.trim() !== '') {
          userName = existingName;
        }
        
        const hasName = userName && userName.trim() !== '' && userName !== 'User' && userName !== 'Account';
        
        state.user = { 
          ...state.user, 
          ...profileData,
          phone: profileData?.phone || state.user?.phone || state.phone,
          name: userName || profileData?.name || state.user?.name || null,
        };
        state.isAuthenticated = true;
        state.profileCompleted = hasName;
        localStorage.setItem('profileCompleted', state.profileCompleted ? 'true' : 'false');
        
        if (state.user) {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
        state.error = null;
        
        console.log('✅ Profile completed after fetch:', state.profileCompleted);
        console.log('✅ User name after fetch:', state.user?.name);
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to get profile';
      })
      
      // ===== UPDATE PROFILE =====
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        const profileData = action.payload?.data || action.payload;
        console.log('📦 Profile updated data:', profileData);
        
        state.user = { 
          ...state.user, 
          ...profileData,
          phone: profileData?.phone || state.user?.phone || state.phone,
          name: profileData?.name || state.user?.name || null,
        };
        state.profileCompleted = true;
        state.isAuthenticated = true;
        state.isNewUser = false;
        state.message = 'Profile updated successfully!';
        
        localStorage.setItem('profileCompleted', 'true');
        localStorage.setItem('isNewUser', 'false');
        
        if (state.user) {
          localStorage.setItem('user', JSON.stringify(state.user));
          const authUser = JSON.parse(localStorage.getItem('tiffinUserLogin') || '{}');
          authUser.name = state.user.name;
          authUser.profile_completed = true;
          localStorage.setItem('tiffinUserLogin', JSON.stringify(authUser));
        }
        state.error = null;
        
        console.log('✅ Profile updated and completed:', state.profileCompleted);
        console.log('✅ User name after update:', state.user?.name);
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update profile';
        state.message = null;
      })
      
      // ===== LOGOUT =====
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        const existingUser = state.user || JSON.parse(localStorage.getItem('user') || '{}');
        const hasProfile = existingUser && 
                          existingUser.name && 
                          existingUser.name.trim() !== '' && 
                          existingUser.name !== 'User' && 
                          existingUser.name !== 'Account';
        
        if (existingUser && hasProfile) {
          localStorage.setItem('user', JSON.stringify(existingUser));
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('isNewUser', 'false');
        }
        
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.phone = '';
        state.otpSent = false;
        state.otpVerified = false;
        state.message = null;
        state.isNewUser = false;
        state.isLoading = false;
        state.error = null;
        state.showLoginModal = false;
        state.profileCompleted = hasProfile;
        
        localStorage.removeItem('token');
        localStorage.removeItem('tiffinPhone');
        localStorage.removeItem('tiffinUserLogin');
        
        console.log('✅ Logout - Profile completed kept:', state.profileCompleted);
      })
      .addCase(logoutUser.rejected, (state) => {
        const existingUser = state.user || JSON.parse(localStorage.getItem('user') || '{}');
        const hasProfile = existingUser && 
                          existingUser.name && 
                          existingUser.name.trim() !== '' && 
                          existingUser.name !== 'User' && 
                          existingUser.name !== 'Account';
        
        if (existingUser && hasProfile) {
          localStorage.setItem('user', JSON.stringify(existingUser));
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('isNewUser', 'false');
        }
        
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.phone = '';
        state.otpSent = false;
        state.otpVerified = false;
        state.message = null;
        state.isNewUser = false;
        state.isLoading = false;
        state.showLoginModal = false;
        state.profileCompleted = hasProfile;
        
        localStorage.removeItem('token');
        localStorage.removeItem('tiffinPhone');
        localStorage.removeItem('tiffinUserLogin');
        
        console.log('✅ Logout (rejected) - Profile completed kept:', state.profileCompleted);
      })
      
      // ===== DELETE ACCOUNT =====
      .addCase(deleteAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.phone = '';
        state.otpSent = false;
        state.otpVerified = false;
        state.isLoading = false;
        state.message = 'Account deleted successfully';
        state.isNewUser = false;
        state.profileCompleted = false;
        state.error = null;
        state.showLoginModal = false;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tiffinPhone');
        localStorage.removeItem('tiffinUserLogin');
        localStorage.removeItem('isNewUser');
        localStorage.removeItem('profileCompleted');
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.phone = '';
        state.otpSent = false;
        state.otpVerified = false;
        state.isNewUser = false;
        state.profileCompleted = false;
        state.showLoginModal = false;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tiffinPhone');
        localStorage.removeItem('tiffinUserLogin');
        localStorage.removeItem('isNewUser');
        localStorage.removeItem('profileCompleted');
      })
      
      // ===== SYNC USER FROM STORAGE =====
      .addCase(syncUserFromStorage.pending, (state) => {
        console.log('🔄 Syncing user from storage...');
      })
      .addCase(syncUserFromStorage.fulfilled, (state, action) => {
        const { user, token } = action.payload;
        console.log('✅ User synced from storage:', user);
        state.user = user;
        state.token = token;
        state.isAuthenticated = true;
        state.profileCompleted = !!(user.name && 
                                   user.name.trim() !== '' && 
                                   user.name !== 'User' && 
                                   user.name !== 'Account');
        state.phone = user.phone || state.phone;
        localStorage.setItem('profileCompleted', state.profileCompleted ? 'true' : 'false');
        localStorage.setItem('user', JSON.stringify(user));
      })
      .addCase(syncUserFromStorage.rejected, (state, action) => {
        console.log('⚠️ No user data to sync:', action.payload);
      });
  },
});

// ✅ Export all actions
export const { 
  setPhone, 
  clearOTP, 
  clearError, 
  clearMessage, 
  resetAuth, 
  setShowLoginModal, 
  setProfileCompleted,
  updateUserName,
  refreshUser,
} = authSlice.actions;

// ✅ Export syncUserFromStorage
export { syncUserFromStorage };

// ✅ Default export
export default authSlice.reducer;